import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, joinUrl, endpoints } from '../api/config';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  Grid,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { BUTTON_STYLE } from '../constants/styles';
import jyBlackLogo from '../assets/JY BLACK.png';
import axios from 'axios';

const SuperAdminRegister = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    company_name: '',
    company_email: '',
    phone_number: '',
    password: '',
    confirm_password: '',
    is_super_admin: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      // Allow spaces only in company_name field
      if (name === 'company_name') {
        setFormData(prev => ({ ...prev, [name]: value }));
      } else if (name === 'username') {
        // Only allow alphanumeric, underscore, and dollar sign in username
        const sanitizedValue = value.replace(/[^a-zA-Z0-9_$]/g, '');
        setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
      } else {
        // Remove spaces from all other fields
        setFormData(prev => ({ ...prev, [name]: value.replace(/\s+/g, '') }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate username
    if (formData.username.includes(' ')) {
      setError('Username cannot contain spaces');
      setLoading(false);
      return;
    }

    // Check if username contains at least one alphanumeric character
    if (!/[a-zA-Z0-9]/.test(formData.username)) {
      setError('Username must contain at least one letter or number');
      setLoading(false);
      return;
    }

    // Check if username contains only valid characters (alphanumeric, underscore, and dollar sign)
    if (!/^[a-zA-Z0-9_$]+$/.test(formData.username)) {
      setError('Username can only contain letters, numbers, underscore (_) and dollar sign ($)');
      setLoading(false);
      return;
    }

    // Validate username length
    if (formData.username.length < 3) {
      setError('Username must be at least 3 characters long');
      setLoading(false);
      return;
    }
    if (formData.username.length > 20) {
      setError('Username cannot exceed 20 characters');
      setLoading(false);
      return;
    }

    // Validate company name
    if (formData.company_name.trim().length < 2) {
      setError('Company name must be at least 2 characters long');
      setLoading(false);
      return;
    }
    if (formData.company_name.trim().length > 50) {
      setError('Company name cannot exceed 50 characters');
      setLoading(false);
      return;
    }

    // Validate company email (no spaces allowed)
    if (formData.company_email.includes(' ')) {
      setError('Email cannot contain spaces');
      setLoading(false);
      return;
    }
    if (!formData.company_email.includes('@') || !formData.company_email.includes('.')) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Validate phone number (no spaces allowed)
    if (formData.phone_number.includes(' ')) {
      setError('Phone number cannot contain spaces');
      setLoading(false);
      return;
    }
    // Only allow digits in phone number
    if (!/^\d+$/.test(formData.phone_number)) {
      setError('Phone number can only contain digits');
      setLoading(false);
      return;
    }
    // Check minimum length
    if (formData.phone_number.length < 10) {
      setError('Phone number must be at least 10 digits');
      setLoading(false);
      return;
    }
    // Check maximum length
    if (formData.phone_number.length > 12) {
      setError('Phone number cannot exceed 12 digits');
      setLoading(false);
      return;
    }

    // Validate passwords match and no spaces
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    if (formData.password.includes(' ') || formData.confirm_password.includes(' ')) {
      setError('Passwords cannot contain spaces');
      setLoading(false);
      return;
    }
    if (formData.password.length < 4) {
      setError('Password must be at least 4 characters long');
      setLoading(false);
      return;
    }

    // Prepare the request payload
    const payload = {
      username: formData.username,
      company_name: formData.company_name.trim(),
      company_email: formData.company_email,
      phone_number: formData.phone_number,
      password: formData.password,
      confirm_password: formData.confirm_password,
      is_super_admin: formData.is_super_admin,
    };

    try {
      // First, check if admin already exists by fetching existing admins
      try {
        const existingAdminsResponse = await axios.get(joinUrl(import.meta.env.VITE_API_URL, '/master-admin-details'));
        console.log('Existing admins:', existingAdminsResponse.data);
        
        if (existingAdminsResponse.data && Array.isArray(existingAdminsResponse.data)) {
          const existingAdmins = existingAdminsResponse.data;
          
          // Check if username already exists
          const usernameExists = existingAdmins.some(admin => 
            admin.username && admin.username.toLowerCase() === formData.username.toLowerCase()
          );
          
          // Check if email already exists
          const emailExists = existingAdmins.some(admin => 
            admin.company_email && admin.company_email.toLowerCase() === formData.company_email.toLowerCase()
          );
          
          if (usernameExists) {
            setError('Username already exists');
            setLoading(false);
            return;
          }
          
          if (emailExists) {
            setError('Email already exists');
            setLoading(false);
            return;
          }
        }
      } catch (checkError) {
        console.log('Could not check existing admins, proceeding with registration:', checkError);
        // Continue with registration even if we can't check existing admins
      }

      // Use direct axios call without authentication for initial admin registration
      const response = await axios.post(joinUrl(import.meta.env.VITE_API_URL, '/admin/register'), payload);

      console.log('Response status:', response.status);
      console.log('Response data:', response.data);

      // Check if the response indicates success
      if (response.data && (response.status === 201 || response.status === 200)) {
        // Check if the response data indicates an error or duplicate
        const responseData = response.data;
        const responseText = JSON.stringify(responseData).toLowerCase();
        
        console.log('Response text:', responseText);
        
        // Check for specific success/error indicators in the response
        const hasError = responseText.includes('duplicate') || 
                        responseText.includes('already exists') ||
                        responseText.includes('username') ||
                        responseText.includes('email') ||
                        responseText.includes('unique constraint') ||
                        responseText.includes('constraint') ||
                        responseText.includes('error') ||
                        responseText.includes('failed') ||
                        responseText.includes('invalid');
        
        // Check for success indicators
        const hasSuccess = responseText.includes('success') ||
                          responseText.includes('created') ||
                          responseText.includes('registered') ||
                          responseData.success === true ||
                          responseData.message?.toLowerCase().includes('success') ||
                          responseData.message?.toLowerCase().includes('created');
        
        console.log('Has error:', hasError);
        console.log('Has success:', hasSuccess);
        
        if (hasError && !hasSuccess) {
          setError('Admin already exists with this username or email');
        } else if (hasSuccess || (!hasError && !hasSuccess)) {
          // Show welcome dialog only on successful creation
          setShowWelcomeDialog(true);
        } else {
          setError('Registration failed. Please try again.');
        }
      } else {
        // Handle unexpected response
        setError('Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      // Handle validation errors from the server
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.detail;
        if (Array.isArray(validationErrors)) {
          setError(validationErrors.map(err => err.msg).join(', '));
        } else if (typeof validationErrors === 'object') {
          const errorMessages = Object.entries(validationErrors)
            .map(([field, message]) => `${field}: ${message}`)
            .join(', ');
          setError(errorMessages);
        } else {
          setError(validationErrors || 'Invalid input data');
        }
      } else if (err.response?.status === 400 || err.response?.status === 409) {
        // Handle conflict errors (409) and bad request errors (400)
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.response?.data?.error || '';
        console.log('Error message:', errorMessage);
        
        if (errorMessage.toLowerCase().includes('duplicate') || 
            errorMessage.toLowerCase().includes('already exists') ||
            errorMessage.toLowerCase().includes('username') ||
            errorMessage.toLowerCase().includes('email') ||
            errorMessage.toLowerCase().includes('unique constraint')) {
          setError('Admin already exists with this username or email');
        } else {
          setError(errorMessage || 'Registration failed. Please try again.');
        }
      } else {
        // Handle other errors
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.response?.data?.error || '';
        console.log('Other error message:', errorMessage);
        
        if (errorMessage.toLowerCase().includes('duplicate') || 
            errorMessage.toLowerCase().includes('already exists') ||
            errorMessage.toLowerCase().includes('username') ||
            errorMessage.toLowerCase().includes('email') ||
            errorMessage.toLowerCase().includes('unique constraint') ||
            errorMessage.toLowerCase().includes('constraint')) {
          setError('Admin already exists with this username or email');
        } else {
          setError(errorMessage || 'Registration failed. Please try again.');
        }
      }
      
      // Additional fallback check for any error that might indicate duplicate
      if (!err.response?.data) {
        const errorText = err.message || err.toString();
        if (errorText.toLowerCase().includes('duplicate') || 
            errorText.toLowerCase().includes('already exists') ||
            errorText.toLowerCase().includes('username') ||
            errorText.toLowerCase().includes('email')) {
          setError('Admin already exists with this username or email');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWelcomeDialogClose = () => {
    setShowWelcomeDialog(false);
    // Redirect to login page after dialog closes
    navigate('/login', { 
      state: { message: 'Super admin registration successful. Please login.' }
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #e3f0ff 0%, #fafcff 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm" sx={{ mb: 4 }}>
        <Paper
          elevation={6}
          sx={{
            p: 5,
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'white',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            minHeight: '600px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              gap: 2,
            }}
          >
            <img 
              src={jyBlackLogo} 
              alt="JY Logo" 
              style={{ 
                width: '90px', 
                height: 'auto',
                marginBottom: '16px'
              }} 
            />
            <Typography 
              component="h1" 
              variant="h5" 
              sx={{ 
                color: 'text.primary', 
                fontWeight: 600,
                textAlign: 'center',
                marginBottom: '16px'
              }}
            >
              Super Admin Registration
            </Typography>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box 
            component="form" 
            onSubmit={handleSubmit} 
            sx={{ 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              <TextField
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                autoComplete="username"
                error={!!error && error.toLowerCase().includes('username')}
                disabled={loading}
                inputProps={{ 
                  maxLength: 20,
                  pattern: "[a-zA-Z0-9_$]+",
                  title: "Username can only contain letters, numbers, underscore (_) and dollar sign ($)"
                }}
                helperText="Must contain at least one letter or number. Only letters, numbers, underscore (_) and dollar sign ($) are allowed."
                sx={{ backgroundColor: 'background.paper' }}
              />
              
              <TextField
                required
                fullWidth
                id="company_name"
                label="Company Name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                autoComplete="organization"
                error={!!error && error.toLowerCase().includes('company_name')}
                disabled={loading}
                inputProps={{ maxLength: 50 }}
                sx={{ backgroundColor: 'background.paper' }}
              />
              
              <TextField
                required
                fullWidth
                id="company_email"
                label="Company Email"
                name="company_email"
                type="email"
                value={formData.company_email}
                onChange={handleChange}
                autoComplete="email"
                error={!!error && error.toLowerCase().includes('email')}
                disabled={loading}
                sx={{ backgroundColor: 'background.paper' }}
              />
              
              <TextField
                required
                fullWidth
                id="phone_number"
                label="Phone Number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                autoComplete="tel"
                error={!!error && error.toLowerCase().includes('phone')}
                disabled={loading}
                inputProps={{ 
                  maxLength: 12,
                  minLength: 10,
                  pattern: "[0-9]{10,12}",
                  title: "Phone number must be between 10 and 12 digits"
                }}
                helperText="Must be between 10 and 12 digits"
                sx={{ backgroundColor: 'background.paper' }}
              />
              
              <TextField
                required
                fullWidth
                id="password"
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={BUTTON_STYLE}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                required
                fullWidth
                id="confirm_password"
                name="confirm_password"
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={handleChange}
                disabled={loading}
                error={formData.password !== formData.confirm_password && formData.confirm_password.length > 0}
                helperText={formData.password !== formData.confirm_password && formData.confirm_password.length > 0 ? "Passwords don't match" : ""}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_super_admin}
                    onChange={handleChange}
                    name="is_super_admin"
                  />
                }
                label="Add as a Super Admin"
              />
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5, 
                fontWeight: 600, 
                fontSize: 16, 
                letterSpacing: 1,
                ...BUTTON_STYLE 
              }}
              disabled={loading}
            >
              {loading ? 'Registering...' : 'Register'}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => navigate('/login')}
              sx={{ 
                mt: 1,
                ...BUTTON_STYLE 
              }}
            >
              Already have an account? Login
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Welcome Dialog */}
      <Dialog
        open={showWelcomeDialog}
        onClose={handleWelcomeDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon 
            sx={{ 
              fontSize: 60, 
              color: 'success.main', 
              mb: 2 
            }} 
          />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
            Welcome to the Admin Portal! 🎉
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            Congratulations! Your super admin account has been successfully created. 
            I'm glad to help you get started with managing your warehouse operations.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            You will now be redirected to the login page where you can sign in with your credentials.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleWelcomeDialogClose}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: 16,
              ...BUTTON_STYLE
            }}
          >
            Continue to Login
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminRegister; 